import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, FlaskConical, Leaf, MapPin, Heart } from "lucide-react";
import { useI18n, tr } from "@/lib/i18n";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import NewsletterForm from "@/components/NewsletterForm";
import { Button } from "@/components/ui/button";

export default function HomePage() {
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/articles", { params: { limit: 3 } }).then((r) => setArticles(r.data || [])).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.status === "published").slice(0, 2);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7 fade-in-up">
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-6">
                {t("home.overline")} · Made in Italy
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-brand">
                {t("home.hero_title")}
              </h1>
              <p className="mt-8 text-lg text-muted-ink max-w-xl leading-relaxed">
                {t("home.hero_sub")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid="hero-cta-pet"
                  className="rounded-full bg-brand text-white hover:bg-[#0f2020] h-12 px-7 text-sm"
                >
                  <Link to="/prodotti?linea=pet">
                    {t("home.hero_cta_pet")}
                    <ArrowUpRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-cta-about"
                  className="rounded-full border-brand text-brand hover:bg-brand hover:text-white h-12 px-7 text-sm"
                >
                  <Link to="/chi-siamo">{t("home.hero_cta_about")}</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div
                className="image-hover-scale aspect-[4/5] bg-cream-warm overflow-hidden"
                data-testid="hero-image"
              >
                <img
                  src="https://images.unsplash.com/photo-1623387641168-d9803ddd3f35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwZG9nJTIwY2F0JTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NDU0NTM3M3ww&ixlib=rb-4.1.0&q=85"
                  alt="Cane e gatto insieme in un contesto naturale"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-soft p-5 max-w-[220px]">
                <div className="text-[10px] uppercase tracking-editorial text-pet mb-1">
                  {lang === "en" ? "Pet Line" : "Linea Pet"}
                </div>
                <p className="text-sm text-brand font-body leading-snug">
                  {lang === "en" ? "ARTICOLAX and FLORAX, available now on Amazon." : "ARTICOLAX e FLORAX, disponibili ora su Amazon."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LINE SELECTOR */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Linee</div>
          <h2 className="font-display text-4xl md:text-5xl text-brand leading-tight">
            {t("home.lines_title")}
          </h2>
          <p className="mt-4 text-muted-ink">{t("home.lines_sub")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/prodotti?linea=pet"
            data-testid="pet-line-selector"
            className="group relative border border-soft bg-white p-8 md:p-10 flex flex-col min-h-[320px] overflow-hidden hover:border-pet transition-colors"
          >
            <div className="absolute top-0 left-0 h-1 w-16 bg-pet" />
            <div className="flex items-center gap-2 text-xs uppercase tracking-editorial text-pet mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-pet" />
              BioNutrix Pet
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-brand mb-3">{lang === "en" ? "For dogs and cats" : "Per cani e gatti"}</h3>
            <p className="text-muted-ink flex-1 max-w-md">{t("home.pet_desc")}</p>
            <div className="mt-6 inline-flex items-center gap-1 text-brand text-sm link-underline w-fit">
              {t("home.pet_cta")} <ArrowUpRight className="h-4 w-4" />
            </div>
          </Link>

          <Link
            to="/linea-human"
            data-testid="human-line-selector"
            className="group relative border border-soft bg-white p-8 md:p-10 flex flex-col min-h-[320px] overflow-hidden hover:border-human transition-colors"
          >
            <div className="absolute top-0 left-0 h-1 w-16 bg-human" />
            <div className="flex items-center gap-2 text-xs uppercase tracking-editorial text-human mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-human" />
              BioNutrix Human
              <span className="ml-2 px-2 py-0.5 text-[10px] bg-human-soft text-human">
                {t("home.coming_soon")}
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-brand mb-3">{lang === "en" ? "For people" : "Per le persone"}</h3>
            <p className="text-muted-ink flex-1 max-w-md">{t("home.human_desc")}</p>
            <div className="mt-6 inline-flex items-center gap-1 text-brand text-sm link-underline w-fit">
              {t("home.human_cta")} <ArrowUpRight className="h-4 w-4" />
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-cream-warm py-16 lg:py-24" data-testid="featured-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">
                {lang === "en" ? "Available products" : "Prodotti disponibili"}
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-brand">{t("home.products_title")}</h2>
              <p className="mt-3 text-muted-ink max-w-lg">{t("home.products_sub")}</p>
            </div>
            <Link
              to="/prodotti"
              className="text-sm text-brand link-underline inline-flex items-center gap-1"
              data-testid="featured-view-all"
            >
              {t("common.view_all")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY BIONUTRIX */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">{lang === "en" ? "Our commitment" : "Il nostro impegno"}</div>
            <h2 className="font-display text-4xl md:text-5xl text-brand leading-tight">
              {t("home.why_title")}
            </h2>
            <p className="mt-4 text-muted-ink">{t("home.why_sub")}</p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            <WhyItem icon={<FlaskConical className="h-5 w-5" />} title={t("home.why_formulas")} desc={t("home.why_formulas_txt")} />
            <WhyItem icon={<Leaf className="h-5 w-5" />} title={t("home.why_ingredients")} desc={t("home.why_ingredients_txt")} />
            <WhyItem icon={<MapPin className="h-5 w-5" />} title={t("home.why_italy")} desc={t("home.why_italy_txt")} />
            <WhyItem icon={<Heart className="h-5 w-5" />} title={t("home.why_care")} desc={t("home.why_care_txt")} />
          </div>
        </div>
      </section>

      {/* NATURE & RESEARCH */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxib3RhbmljYWwlMjBsZWF2ZXMlMjB0ZXh0dXJlJTIwbGFib3JhdG9yeXxlbnwwfHx8fDE3ODQ1NDUzNzR8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0F1F1F]/70" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 text-white">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-editorial text-[#B7C3C1] mb-4">
              {lang === "en" ? "Nature + Science" : "Natura + Scienza"}
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              {t("home.nature_title")}
            </h2>
            <p className="mt-6 text-[#D5DDDA] leading-relaxed max-w-xl">{t("home.nature_txt")}</p>
            <Link
              to="/scienza-qualita"
              className="mt-8 inline-flex items-center gap-1 text-white link-underline text-sm"
            >
              {t("nav.science")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      {articles.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24" data-testid="journal-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Journal</div>
              <h2 className="font-display text-4xl md:text-5xl text-brand">{t("home.journal_title")}</h2>
              <p className="mt-3 text-muted-ink max-w-lg">{t("home.journal_sub")}</p>
            </div>
            <Link to="/journal" className="text-sm text-brand link-underline inline-flex items-center gap-1">
              {t("common.view_all")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`/journal/${a.slug}`}
                className="group border border-soft bg-white flex flex-col"
                data-testid={`journal-card-${a.slug}`}
              >
                <div className="image-hover-scale aspect-[16/10] overflow-hidden bg-cream-warm">
                  <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-editorial text-muted-ink mb-2">
                    {a.category} · {a.read_time} min
                  </div>
                  <h3 className="font-display text-2xl text-brand leading-tight">{a.title}</h3>
                  <p className="mt-2 text-sm text-muted-ink flex-1">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PROFESSIONALS */}
      <section className="bg-brand text-white py-16 lg:py-24" data-testid="pro-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-editorial text-[#B7C3C1] mb-3">B2B</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">{t("home.pro_title")}</h2>
            <p className="mt-4 text-[#D5DDDA] max-w-2xl">{t("home.pro_txt")}</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Button
              asChild
              data-testid="pro-cta-button"
              className="rounded-full bg-white text-brand hover:bg-[#E5EBEA] h-12 px-7 text-sm"
            >
              <Link to="/professionisti">{t("home.pro_cta")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Newsletter</div>
            <h2 className="font-display text-4xl md:text-5xl text-brand leading-tight">
              {t("home.newsletter_title")}
            </h2>
            <p className="mt-4 text-muted-ink max-w-md">{t("home.newsletter_sub")}</p>
          </div>
          <div className="md:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function WhyItem({ icon, title, desc }) {
  return (
    <div>
      <div className="h-10 w-10 border border-brand text-brand flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-display text-2xl text-brand mb-2">{title}</h3>
      <p className="text-sm text-muted-ink leading-relaxed">{desc}</p>
    </div>
  );
}
